import React, { useState, useEffect } from 'react';
import { Loader2, FileText, CheckCircle2, Clock } from 'lucide-react';

interface CaprionStatusLoaderProps {
  onComplete?: () => void;
  onError?: (error: string) => void;
  customSteps?: Array<{ icon: any; text: string; duration: number }>;
  /**
   * Heading above the animation, telling the user WHICH check is running.
   * Both the Bureau and the AA polling pages render this same card, so without
   * it the two are indistinguishable.
   */
  title?: string;
}

const CaprionStatusLoader: React.FC<CaprionStatusLoaderProps> = ({ onComplete, onError, customSteps, title }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  // Steps for Caprion application processing (default or custom)
  const steps = customSteps || [
    { icon: FileText, text: 'Submitting your application', duration: 2000 },
    { icon: Clock, text: 'Processing with lender', duration: 3000 },
    { icon: CheckCircle2, text: 'Verifying application status', duration: 3000 },
  ];

  // Animate progress bar smoothly with easing
  useEffect(() => {
    const targetProgress = Math.min(((currentStep + 1) / steps.length) * 85, 85); // Cap at 85%
    const startProgress = progress;
    const duration = steps[currentStep]?.duration || 2000;
    const startTime = Date.now();

    const animateProgress = () => {
      const elapsed = Date.now() - startTime;
      const progressRatio = Math.min(elapsed / duration, 1);
      
      // Ease-out cubic for smooth deceleration
      const easedProgress = 1 - Math.pow(1 - progressRatio, 3);
      const newProgress = startProgress + (targetProgress - startProgress) * easedProgress;
      
      setProgress(newProgress);

      if (progressRatio < 1) {
        requestAnimationFrame(animateProgress);
      }
    };

    requestAnimationFrame(animateProgress);
  }, [currentStep, steps]);

  // Step progression
  useEffect(() => {
    if (currentStep < steps.length - 1) {
      const timer = setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, steps[currentStep].duration);

      return () => clearTimeout(timer);
    }
  }, [currentStep, steps]);

  const CurrentIcon = steps[currentStep].icon;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
        {/* Page heading — identifies which verification this card belongs to */}
        {title && (
          <p className="mb-5 border-b pb-3 text-center text-base font-bold text-gray-800">
            {title}
          </p>
        )}

        {/* Icon Animation */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            {/* Outer rotating ring. Only the TOP edge is solid — a uniform ring
                looks static no matter how fast it spins, which made the card feel
                frozen. Faster (1.2s) for the same reason. */}
            <div className="absolute inset-0 rounded-full border-4 border-intellend-green/15 border-t-intellend-green animate-spin"
                 style={{ animationDuration: '1.2s' }} />
            
            {/* Inner circle. The icon itself fades/scales in whenever the step
                changes, so the card visibly "ticks over" between steps. The old
                animate-pulse on the whole circle just throbbed in place. */}
            <div className="relative bg-intellend-green/5 rounded-full p-6">
              <CurrentIcon
                key={currentStep}
                className="w-12 h-12 text-intellend-green animate-in fade-in zoom-in-50 duration-500"
              />
            </div>

            {/* Processing animation for middle step */}
            {currentStep === 1 && (
              <div className="absolute -right-2 -top-2">
                <div className="bg-intellend-green rounded-full p-2 animate-bounce">
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Current Step Text */}
        <div className="text-center space-y-3">
          <h3 className="text-xl font-semibold text-gray-900 animate-in fade-in slide-in-from-bottom-2 duration-500">
            {steps[currentStep].text}
          </h3>
          
          {/* Progress dots */}
          <div className="flex justify-center gap-2 pt-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentStep
                    ? 'w-8 bg-intellend-green'
                    : index < currentStep
                    ? 'w-2 bg-intellend-green/60'
                    : 'w-2 bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Additional context text */}
          <p className="text-sm text-gray-500 pt-2">
            {currentStep === 0 
              ? 'Creating your loan application...'
              : currentStep === 1
              ? 'This may take a few moments...'
              : 'Almost done...'}
          </p>
        </div>

        {/* Animated loading bar. The fill caps at 85% and then holds, so a plain
            static bar read as "stuck" — the pulsing leading edge keeps it alive
            while we wait on the lender. */}
        <div className="mt-6 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="relative h-full bg-gradient-to-r from-intellend-green to-intellend-green/80 rounded-full"
            style={{
              width: `${progress}%`,
              transition: 'none', // Smooth animation handled by requestAnimationFrame
            }}
          >
            <span className="absolute inset-y-0 right-0 w-10 rounded-full bg-white/60 animate-pulse" />
          </div>
        </div>

        {/* Info message */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-400">
            Please do not close this window
          </p>
        </div>
      </div>
    </div>
  );
};

export default CaprionStatusLoader;
